import type {
  PainelData,
  PainelFilaItem,
  PainelTakeoutProntoItem,
} from "../../../../services/painelService";
import type { FornoItem } from "../../../../services/fornoService";

export interface ClientePedidoStatusRow {
  displayId: number;
  customerName: string;
  /** ISO — quando entrou na fila de montagem (`createdAt` do item na fila). */
  entrouEmPreparoEm?: string;
  /** ISO — `PRODUCOES.data_fim` (takeout via `/painel/takeouts-prontos`). */
  prontoParaRetiradaEm?: string;
}

/** Ranking retirada: só `PEDIDOS_INTEGRACAO.order_type === takeout`. */
function isPedidoTvTakeout(
  pedido: { orderType?: string | null } | null | undefined
): boolean {
  return (pedido?.orderType ?? "").trim().toLowerCase() === "takeout";
}

function nomeCliente(
  pedidoIntegracao: { customerName?: string | null } | null | undefined
): string {
  const n = pedidoIntegracao?.customerName?.trim();
  if (n) return n;
  return "Cliente";
}

function pickCustomerName(a: string, b: string): string {
  if (a !== "Cliente") return a;
  return b;
}

function earliestIso(
  a: string | undefined,
  b: string | undefined
): string | undefined {
  if (!a?.trim()) return b;
  if (!b?.trim()) return a;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isNaN(da)) return b;
  if (Number.isNaN(db)) return a;
  return da <= db ? a : b;
}

function ordenarPorFilaTemporal(
  rows: ClientePedidoStatusRow[],
  campo: keyof Pick<
    ClientePedidoStatusRow,
    "entrouEmPreparoEm" | "prontoParaRetiradaEm"
  >
): ClientePedidoStatusRow[] {
  const ordenado = [...rows];
  ordenado.sort((a, b) => {
    const isoA = a[campo];
    const isoB = b[campo];
    const ta = isoA?.trim()
      ? new Date(isoA).getTime()
      : Number.POSITIVE_INFINITY;
    const tb = isoB?.trim()
      ? new Date(isoB).getTime()
      : Number.POSITIVE_INFINITY;
    const na = Number.isNaN(ta) ? Number.POSITIVE_INFINITY : ta;
    const nb = Number.isNaN(tb) ? Number.POSITIVE_INFINITY : tb;
    if (na !== nb) return na - nb;
    return (a.displayId ?? 0) - (b.displayId ?? 0);
  });
  return ordenado;
}

function mergePreparoRows(
  rows: ClientePedidoStatusRow[]
): ClientePedidoStatusRow[] {
  const map = new Map<number, ClientePedidoStatusRow>();
  for (const row of rows) {
    const prev = map.get(row.displayId);
    if (!prev) {
      map.set(row.displayId, { ...row });
      continue;
    }
    map.set(row.displayId, {
      displayId: row.displayId,
      customerName: pickCustomerName(prev.customerName, row.customerName),
      entrouEmPreparoEm: earliestIso(
        prev.entrouEmPreparoEm,
        row.entrouEmPreparoEm
      ),
    });
  }
  return ordenarPorFilaTemporal(Array.from(map.values()), "entrouEmPreparoEm");
}

function entrouEmPreparoFromFila(item: PainelFilaItem): string {
  let t: string | undefined = item.createdAt;
  for (const m of item.montagens ?? []) {
    if (m.startedAt) t = earliestIso(t, m.startedAt);
  }
  return t ?? item.createdAt;
}

function filaRow(item: PainelFilaItem): ClientePedidoStatusRow | null {
  if (!isPedidoTvTakeout(item.pedidoIntegracao)) return null;
  if (item.displayId == null) return null;
  return {
    displayId: item.displayId,
    customerName: nomeCliente(item.pedidoIntegracao),
    entrouEmPreparoEm: entrouEmPreparoFromFila(item),
  };
}

function fornoRow(item: FornoItem): ClientePedidoStatusRow | null {
  if (!isPedidoTvTakeout(item.pedidoIntegracao)) return null;
  if (item.displayId == null) return null;
  const entrouEmPreparoEm =
    item.filaCreatedAt?.trim() ||
    item.montagemStartedAt?.trim() ||
    item.createdAt;
  return {
    displayId: item.displayId,
    customerName: nomeCliente(item.pedidoIntegracao),
    entrouEmPreparoEm,
  };
}

export function painelTakeoutsProntosParaRows(
  itens: PainelTakeoutProntoItem[]
): ClientePedidoStatusRow[] {
  const rows: ClientePedidoStatusRow[] = itens.map((t) => ({
    displayId: t.displayId,
    customerName: t.customerName?.trim() || "Cliente",
    prontoParaRetiradaEm: t.prontoEm,
  }));
  return ordenarPorFilaTemporal(rows, "prontoParaRetiradaEm");
}

/**
 * TV **`/ranking/retirada`** apenas — takeout e coluna Prontos = `PainelData.takeoutsProntos`
 * (`PRODUCOES.data_fim` via backend). Enquanto o número está em montagem/forno, não duplica em Prontos.
 */
export function mapPainelParaColunasCliente(data: PainelData | null): {
  emPreparo: ClientePedidoStatusRow[];
  prontos: ClientePedidoStatusRow[];
} {
  if (!data) {
    return { emPreparo: [], prontos: [] };
  }

  const preparoRaw: ClientePedidoStatusRow[] = [];
  for (const item of data.aguardandoMontagem) {
    const r = filaRow(item);
    if (r) preparoRaw.push(r);
  }
  for (const item of data.emMontagem) {
    const r = filaRow(item);
    if (r) preparoRaw.push(r);
  }
  for (const item of data.emForno) {
    const r = fornoRow(item);
    if (r) preparoRaw.push(r);
  }

  const emPreparo = mergePreparoRows(preparoRaw);
  const idsEmPreparo = new Set(emPreparo.map((r) => r.displayId));

  const prontosBase = painelTakeoutsProntosParaRows(
    Array.isArray(data.takeoutsProntos) ? data.takeoutsProntos : []
  );
  const prontos = prontosBase.filter((r) => !idsEmPreparo.has(r.displayId));

  return { emPreparo, prontos };
}
